// Pure feasibility calculation. Zero dependencies — runs identically in the
// browser bundle (Vite) and Pages Functions (Workers runtime).
//
// The formulas mirror the Bluehills "Tick Flick Feaso" sheet:
//   gross_sales              = dwellings × sale_price_per_dwelling
//   selling_costs            = gross_sales_after_contingency × selling_cost_pct
//   net_sales                = gross_sales_after_contingency − selling_costs
//   stamp_duty               = purchase_price × stamp_duty_pct
//   site_total               = purchase_price + stamp_duty
//   construction             = dwellings × dwelling_m2 × cost_per_m2
//   construction_contingency = construction × construction_contingency_pct
//   interest_land            = site_total × land_loan_rate × (holding_months/12)
//   interest_dev             = construction × dev_loan_rate × (holding_months/12)
//   dev_costs                = sum of all dev-cost line items (incl. interest)
//   gst_liability            = (net_sales − (dev_costs − interest)) / 11   [margin scheme]
//   gst_margin_credit        = purchase_price / 11                          [margin scheme]
//   forecast_profit          = net_sales − dev_costs − site_total − gst_liability + gst_margin_credit
//   profit_on_cost_pct       = forecast_profit / (site_total + dev_costs)

import {
  TIER1_DEFAULTS,
  TIER2_DEFAULTS,
  subdivisionFee,
} from './defaults.js';

/**
 * @typedef {Object} FeasoInputs
 * @property {number} purchase_price
 * @property {number} dwellings
 * @property {number} sale_price_per_dwelling
 * @property {number} [dwelling_m2]                 // Tier 2 override
 * @property {number} [cost_per_m2]                 // Tier 2 override
 * @property {number} [sale_price_contingency_pct]  // default 0
 * @property {number} [holding_months]              // default 12
 * @property {Object} [overrides]                   // Any other Tier 1 or Tier 2 key
 */

export function calculateFeaso(inputs) {
  const cfg = resolveConfig(inputs);

  // 1. Sales
  const gross_sales = cfg.dwellings * cfg.sale_price_per_dwelling;
  const sale_contingency_reduction =
    gross_sales * cfg.sale_price_contingency_pct;
  const gross_sales_after_contingency = gross_sales - sale_contingency_reduction;
  const selling_costs = gross_sales_after_contingency * cfg.selling_cost_pct;
  const net_sales = gross_sales_after_contingency - selling_costs;

  // 2. Site acquisition
  const stamp_duty = cfg.purchase_price * cfg.stamp_duty_pct;
  const site_total = cfg.purchase_price + stamp_duty;

  // 3. Construction
  const construction = cfg.dwellings * cfg.dwelling_m2 * cfg.cost_per_m2;
  const construction_contingency =
    construction * cfg.construction_contingency_pct;

  // 4. Subdivision
  const subdivision = cfg.subdivision ?? subdivisionFee(cfg.dwellings);

  // 5. Interest (annualised × holding_months/12)
  const hold_factor = cfg.holding_months / 12;
  const interest_land = site_total * cfg.land_loan_rate * hold_factor;
  const interest_dev = construction * cfg.dev_loan_rate * hold_factor;
  const interest = interest_land + interest_dev;

  // 6. Dev costs (order matches Bluehills sheet for readability)
  const dev_cost_breakdown = {
    design_approvals: cfg.design_approvals,
    subdivision,
    marketing: cfg.marketing,
    authorities: cfg.authorities,
    finance_fees: cfg.finance_fees,
    interest,
    demolition: cfg.demolition,
    construction,
    construction_contingency,
  };
  const dev_costs = Object.values(dev_cost_breakdown).reduce(
    (a, b) => a + b,
    0,
  );

  // 7. GST (margin scheme)
  let gst_liability, gst_margin_credit;
  if (cfg.gst_margin_scheme) {
    const non_interest_dev = dev_costs - interest;
    gst_liability = (net_sales - non_interest_dev) / 11;
    gst_margin_credit = cfg.purchase_price / 11;
  } else {
    gst_liability = net_sales / 11;
    gst_margin_credit = 0;
  }
  const net_gst = gst_liability - gst_margin_credit;

  // 8. Profit
  const forecast_profit =
    net_sales - dev_costs - site_total - gst_liability + gst_margin_credit;
  const total_cost_basis = site_total + dev_costs;
  const profit_on_cost_pct = forecast_profit / total_cost_basis;

  return {
    inputs: cfg,
    sales: {
      gross_sales,
      sale_contingency_reduction,
      gross_sales_after_contingency,
      selling_costs,
      net_sales,
    },
    site_acquisition: {
      purchase_price: cfg.purchase_price,
      stamp_duty,
      total: site_total,
    },
    dev_cost_breakdown,
    dev_costs,
    interest_breakdown: {
      land: interest_land,
      development: interest_dev,
      total: interest,
    },
    gst: {
      liability: gst_liability,
      margin_credit: gst_margin_credit,
      net: net_gst,
    },
    forecast_profit,
    total_cost_basis,
    profit_on_cost_pct,
  };
}

function resolveConfig(inputs) {
  const o = inputs.overrides ?? {};
  return {
    // Tier 3 (required)
    purchase_price: required(inputs.purchase_price, 'purchase_price'),
    dwellings: required(inputs.dwellings, 'dwellings'),
    sale_price_per_dwelling: required(
      inputs.sale_price_per_dwelling,
      'sale_price_per_dwelling',
    ),

    // Tier 3 (optional)
    sale_price_contingency_pct:
      inputs.sale_price_contingency_pct ?? o.sale_price_contingency_pct ?? 0,
    holding_months:
      inputs.holding_months ?? o.holding_months ?? TIER2_DEFAULTS.holding_months,

    // Tier 2 (input wins, then override, then default)
    dwelling_m2: inputs.dwelling_m2 ?? o.dwelling_m2 ?? TIER2_DEFAULTS.dwelling_m2,
    cost_per_m2: inputs.cost_per_m2 ?? o.cost_per_m2 ?? TIER2_DEFAULTS.cost_per_m2,
    demolition: o.demolition ?? TIER2_DEFAULTS.demolition,
    design_approvals: o.design_approvals ?? TIER2_DEFAULTS.design_approvals,
    finance_fees: o.finance_fees ?? TIER2_DEFAULTS.finance_fees,
    marketing: o.marketing ?? TIER2_DEFAULTS.marketing,
    authorities: o.authorities ?? TIER2_DEFAULTS.authorities,
    subdivision: o.subdivision, // explicit override only; else computed

    // Tier 1 (override, then default)
    stamp_duty_pct: o.stamp_duty_pct ?? TIER1_DEFAULTS.stamp_duty_pct,
    selling_cost_pct: o.selling_cost_pct ?? TIER1_DEFAULTS.selling_cost_pct,
    construction_contingency_pct:
      o.construction_contingency_pct ??
      TIER1_DEFAULTS.construction_contingency_pct,
    land_loan_rate: o.land_loan_rate ?? TIER1_DEFAULTS.land_loan_rate,
    dev_loan_rate: o.dev_loan_rate ?? TIER1_DEFAULTS.dev_loan_rate,
    gst_margin_scheme:
      o.gst_margin_scheme ?? TIER1_DEFAULTS.gst_margin_scheme,
  };
}

function required(value, name) {
  if (value === undefined || value === null || Number.isNaN(value)) {
    throw new Error(`calculateFeaso: missing required input "${name}"`);
  }
  return value;
}
